import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "searchClear"]

  static values = {
    companyId: Number,
    locale: { type: String, default: "en" },
    initialSort: { type: String, default: "" }
  }

  connect() {
    this._sortState = []
    this._projectId = null
    this._formId = null
    this._trashRows = []
    this._bindRowHover()
    this._restoreSort()
    this._showPendingFlash()
  }

  // Restoring a row reloads the page, so its toast has to survive the trip.
  _showPendingFlash() {
    const message = sessionStorage.getItem("uniFlash")
    if (!message) return
    sessionStorage.removeItem("uniFlash")
    this._showToast(message, "notice")
  }

  _restoreSort() {
    if (!this.initialSortValue) return
    const parts = this.initialSortValue.split(",").map(s => s.trim())
    if (parts.length < 2) return
    const [colName, direction] = parts

    const table = this.element.querySelector("table")
    if (!table) return
    const th = table.querySelector(`th[data-col-name="${colName}"]`)
    if (!th) return

    // Set opposite direction so sortColumn toggles to the desired one
    th.dataset.sort = direction === "desc" ? "asc" : "desc"
    this._sortState = [colName, direction]
    this._sortTable(th)
    th.dataset.sort = direction
  }

  // ─── Sort Persistence ─────────────────────────────────────────────

  sortColumn(e) {
    const th = e.currentTarget
    const colName = th.dataset.colName
    if (!colName) return

    let currentSort = th.dataset.sort || "asc"
    currentSort = currentSort === "asc" ? "desc" : "asc"
    th.dataset.sort = currentSort

    this._sortState = [colName, currentSort]

    // Save sort
    const tableType = th.closest("table")?.dataset.tableType || "active"
    const urls = {
      pending: `/companies/update_uni_pending_dashboard_sort`,
      active: `/companies/update_uni_active_dashboard_sort`,
      completed: `/companies/update_uni_completed_dashboard_sort`,
      archived: `/companies/update_uni_archive_dashboard_sort`
    }

    const url = urls[tableType]
    if (url) {
      const formData = new FormData()
      formData.append("sort_col_name_and_direction", `${colName}, ${currentSort}`)
      fetch(url, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this._csrfToken() }
      })
    }

    // Client-side sort
    this._sortTable(th)
  }

  _sortTable(th) {
    const table = th.closest("table")
    const tbody = table.querySelector("tbody")
    const rows = Array.from(tbody.querySelectorAll("tr"))
    const colIndex = Array.from(th.parentElement.children).indexOf(th)
    const direction = th.dataset.sort === "desc" ? -1 : 1

    rows.sort((a, b) => {
      const aText = (a.children[colIndex]?.textContent || "").trim().toLowerCase()
      const bText = (b.children[colIndex]?.textContent || "").trim().toLowerCase()
      if (aText < bText) return -1 * direction
      if (aText > bText) return 1 * direction
      return 0
    })

    rows.forEach(row => tbody.appendChild(row))
  }

  // ─── Edit Project Modal (Active/Completed) ──────────────────────

  openEditProject(e) {
    const btn = e.currentTarget
    this._projectId = btn.dataset.projectId
    const modal = document.getElementById("uniEditProjectModal")
    modal.querySelector(".project_number").value = btn.dataset.projectNumber || ""
    const pmSelect = modal.querySelector(".pm_to_assign_active")
    if (pmSelect) {
      const pmEmail = btn.dataset.pmEmail
      for (const opt of pmSelect.options) {
        if (opt.value.includes(pmEmail)) {
          opt.selected = true
          break
        }
      }
    }
    modal.querySelector(".project_contact").value = btn.dataset.contact || ""
    modal.querySelector(".notes").value = btn.dataset.notes || ""
    const statusSelect = modal.querySelector(".js-status")
    if (statusSelect) statusSelect.value = "Active"
    this._showModal("uniEditProjectModal")
  }

  saveEditProject() {
    const modal = document.getElementById("uniEditProjectModal")
    const btn = modal.querySelector(".js-update-project-details")
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const formData = new FormData()
    formData.append("project_id", this._projectId)
    formData.append("project_num", modal.querySelector(".project_number").value)
    formData.append("pm_info", modal.querySelector(".pm_to_assign_active").value)
    formData.append("contact", modal.querySelector(".project_contact").value)
    formData.append("notes", modal.querySelector(".notes").value)
    formData.append("status", modal.querySelector(".js-status").value)

    fetch(`/company/update_project_number`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(() => {
      this._showToast("Updated successfully", "notice")
      window.location.reload()
    })
  }

  // ─── Committee Modal (Pending/Archived) ─────────────────────────

  openCommitteeModal(e) {
    this._formId = e.currentTarget.dataset.formId
    this._showModal("uniCommitteeModal")
  }

  onCommitteeChange(e) {
    const val = e.currentTarget.value
    const btn = document.querySelector(".js-save-row-committee")
    if (btn) {
      btn.textContent = (val === "Approved" || val === "Rejected") ? "NEXT STEP" : "SAVE"
    }
  }

  saveCommittee() {
    const modal = document.getElementById("uniCommitteeModal")
    const btn = modal.querySelector(".js-save-row-committee")
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const committee = modal.querySelector("#committee").value
    const comments = modal.querySelector("#comment").value

    const formData = new FormData()
    formData.append("form_id", this._formId)
    formData.append("committee", committee)
    formData.append("comments", comments)

    fetch(`/company/update_unis_form_committee`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(r => r.json()).then(data => {
      this._showToast("Saved successfully", "notice")
      if (committee === "Approved" || committee === "Rejected") {
        this._hideModal("uniCommitteeModal")
        // Fill email modal
        const emailModal = document.getElementById("uniCommitteeEmailModal")
        emailModal.querySelector(".js-emails-to").value = data.email_to || ""
        emailModal.querySelector(".js-emails-cc").value = data.emails_cc || ""
        emailModal.querySelector(".js-subject").value = data.subject || ""
        emailModal.querySelector(".js-message").value = data.message || ""
        this._showModal("uniCommitteeEmailModal")
      } else {
        window.location.reload()
      }
    })
  }

  // ─── Committee Email Modal ─────────────────────────────────────

  sendCommitteeEmail() {
    const modal = document.getElementById("uniCommitteeEmailModal")
    const btn = modal.querySelector(".js-committee-send-email")
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const formData = new FormData()
    formData.append("form_id", this._formId)
    formData.append("email_to", modal.querySelector(".js-emails-to").value)
    formData.append("emails_cc", modal.querySelector(".js-emails-cc").value)
    formData.append("subject", modal.querySelector(".js-subject").value)
    formData.append("message", modal.querySelector(".js-message").value)

    fetch(`/company/send_unis_committee_email`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(() => {
      this._showToast("Sent successfully", "notice")
      window.location.reload()
    })
  }

  skipCommitteeEmail() {
    this._hideModal("uniCommitteeEmailModal")
    window.location.reload()
  }

  // ─── Assign PM to Form (for PM signature request) ─────────────

  openAssignPmToForm(e) {
    this._formId = e.currentTarget.dataset.formId
    this._showModal("uniAssignPmToFormModal")
    // Reassigning: preselect whoever holds it now, so the change is a
    // deliberate pick rather than defaulting back to the first option.
    const current = e.currentTarget.dataset.currentPm
    if (current) {
      const select = document.getElementById("pm_to_assign_form")
      const match = select && Array.from(select.options)
        .find((option) => option.value.split("||")[0] === current)
      if (match) select.value = match.value
    }
  }

  saveAssignPmToForm() {
    const modal = document.getElementById("uniAssignPmToFormModal")
    const select = modal.querySelector("#pm_to_assign_form")
    if (!select?.value) return

    const btn = modal.querySelector(".js-assign-pm-form")
    const originalLabel = btn.innerHTML
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const formData = new FormData()
    formData.append("form_id", this._formId)
    formData.append("pm_info", select.value)

    fetch(`/assign_pm_to`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(response => {
      if (!response.ok) throw new Error(`Request failed (${response.status})`)
      this._showToast("PM assigned successfully", "notice")
      window.location.reload()
    }).catch(() => {
      this._showToast("Could not assign PM. Please try again.", "alert")
      btn.innerHTML = originalLabel
      btn.disabled = false
    })
  }

  // ─── Assign PM (Pending → Activate) ───────────────────────────

  openAssignPmModal(e) {
    this._formId = e.currentTarget.dataset.formId
    this._showModal("uniAssignPmModal")
  }

  activateAssignPm() {
    const modal = document.getElementById("uniAssignPmModal")
    const select = modal.querySelector("#pm_to_assign_activate")
    if (!select?.value) return

    const btn = modal.querySelector(".js-assign-pm-activate")
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const formData = new FormData()
    formData.append("form_id", this._formId)
    formData.append("pm_to_assign", select.value)

    fetch(`/company/unis_activate_assign_pm`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(() => {
      this._showToast("Assigned successfully", "notice")
      window.location.reload()
    })
  }

  // ─── Resend Email ──────────────────────────────────────────────

  resendEmail(e) {
    const btn = e.currentTarget
    const row = btn.closest("[data-form-id], .js-data-row")
    const formId = row?.dataset.formId || row?.dataset.id
    const pmEmail = btn.dataset.email
    const signerClass = btn.dataset.signerType

    this._openConfirmModal("Resend Email", "The email will be resent.", () => {
      const formData = new FormData()
      formData.append("form_id", formId)
      formData.append("pm_email", pmEmail || "")
      formData.append("next_signer", signerClass || "")

      fetch(`/design/resend_custom_form_email`, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this._csrfToken() }
      }).then(() => {
        this._showToast("Email resent", "notice")
      })
    })
  }

  // ─── Delete Form ──────────────────────────────────────────────

  deleteForm(e) {
    const btn = e.currentTarget
    const formId = btn.dataset.formId
    const row = btn.closest("tr")

    this._openConfirmModal("Delete", "Are you sure you want to delete this form?", () => {
      const formData = new FormData()
      formData.append("form_id", formId)

      fetch(`/design/destroy_custom_form`, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this._csrfToken() }
      }).then(() => {
        row?.remove()
        this._showToast("Removed successfully", "notice")
        this._hideModal("uniConfirmModal")
      })
    })
  }

  // ─── VP List ──────────────────────────────────────────────────

  openVpList() {
    this._showModal("uniVpsListModal")
  }

  addVpRow() {
    const container = document.querySelector(".js-vps")
    const hasRows = container.querySelectorAll(".js-vp-row").length > 0

    const row = document.createElement("div")
    row.className = "js-vp-row flex items-end gap-3 mb-2"
    row.innerHTML = `
      <div class="flex-1">
        ${!hasRows ? '<label class="block text-[13px] text-gray-500 mb-1">Full Name</label>' : ''}
        <input class="vp-full-name w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" type="text">
      </div>
      <div class="flex-[2]">
        ${!hasRows ? '<label class="block text-[13px] text-gray-500 mb-1">Title</label>' : ''}
        <input class="vp-title w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" type="text">
      </div>
      <div class="flex-1">
        ${!hasRows ? '<label class="block text-[13px] text-gray-500 mb-1">Email</label>' : ''}
        <div class="flex items-center gap-2">
          <input class="vp-email w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" type="text">
          <button type="button" class="text-gray-400 hover:text-red-500 cursor-pointer" data-action="click->uni-dashboard#removeVpRow">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          </button>
        </div>
      </div>
    `
    container.appendChild(row)
  }

  removeVpRow(e) {
    e.currentTarget.closest(".js-vp-row")?.remove()
  }

  saveVpList() {
    const btn = document.querySelector(".js-vps-save")
    btn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'
    btn.disabled = true

    const rows = []
    document.querySelectorAll("#uniVpsListModal .js-vp-row").forEach(el => {
      rows.push([
        el.querySelector(".vp-full-name")?.value || "",
        el.querySelector(".vp-title")?.value || "",
        el.querySelector(".vp-email")?.value || ""
      ])
    })

    const formData = new FormData()
    formData.append("rows", JSON.stringify(rows))

    fetch(`/company/update_vps_list`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(() => {
      this._showToast("Saved successfully", "notice")
      window.location.reload()
    })
  }

  // ─── Pending Checkboxes ─────────────────────────────────────

  toggleFilter(e) {
    const checkbox = e.currentTarget
    const filterClass = checkbox.dataset.filterClass
    this.element.querySelectorAll(`.js-data-row.${filterClass}`).forEach(row => {
      row.dataset.hiddenByFilter = checkbox.checked ? "" : "1"
    })
    this._applyRowVisibility()
  }

  // ─── Search ───────────────────────────────────────────────────

  // Filters the table down to rows containing every word typed, in any
  // column. Cheap enough to run on each keystroke — these tables are a few
  // hundred rows at most.
  searchRows() {
    const query = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    const terms = query.split(/\s+/).filter(Boolean)

    this.element.querySelectorAll(".js-data-row").forEach(row => {
      if (!row.dataset.searchText) {
        row.dataset.searchText = row.textContent.replace(/\s+/g, " ").trim().toLowerCase()
      }
      const haystack = row.dataset.searchText
      const matches = terms.every(term => haystack.includes(term))
      row.dataset.hiddenBySearch = matches ? "" : "1"
    })

    if (this.hasSearchClearTarget) {
      this.searchClearTarget.classList.toggle("hidden", terms.length === 0)
      this.searchClearTarget.classList.toggle("flex", terms.length > 0)
    }
    this._applyRowVisibility()
  }

  clearSearch() {
    if (!this.hasSearchTarget) return
    this.searchTarget.value = ""
    this.searchRows()
    this.searchTarget.focus()
  }

  // A row shows only when neither the checkbox filters nor the search box
  // is hiding it — they both used to fight over `style.display`.
  _applyRowVisibility() {
    this.element.querySelectorAll(".js-data-row").forEach(row => {
      const hidden = row.dataset.hiddenByFilter === "1" || row.dataset.hiddenBySearch === "1"
      row.style.display = hidden ? "none" : ""
    })
  }

  // ─── Trash ────────────────────────────────────────────────────

  openTrash() {
    this._showModal("uniTrashModal")
    const modal = document.getElementById("uniTrashModal")
    const body = modal.querySelector(".js-trash-rows")
    const loading = modal.querySelector(".js-trash-loading")
    const empty = modal.querySelector(".js-trash-empty")

    body.innerHTML = ""
    empty.classList.add("hidden")
    loading.classList.remove("hidden")

    fetch("/companies/uni_trash", { headers: { Accept: "application/json" } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        loading.classList.add("hidden")
        this._trashRows = data.rows || []
        this._renderTrash(this._trashRows)
      })
      .catch(() => {
        loading.classList.add("hidden")
        this._showToast("Could not load the trash", "alert")
      })
  }

  closeTrash() {
    this._hideModal("uniTrashModal")
  }

  searchTrash(e) {
    const terms = e.currentTarget.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const rows = (this._trashRows || []).filter(row => {
      const haystack = [row.title, row.room, row.department, row.submitted_by, row.pm,
                        row.type, row.status, row.created_at, row.deleted_by, row.deleted_at]
        .filter(Boolean).join(" ").toLowerCase()
      return terms.every(term => haystack.includes(term))
    })
    this._renderTrash(rows)
  }

  _renderTrash(rows) {
    const modal = document.getElementById("uniTrashModal")
    const body = modal.querySelector(".js-trash-rows")
    const empty = modal.querySelector(".js-trash-empty")

    body.innerHTML = ""
    empty.classList.toggle("hidden", rows.length > 0)

    rows.forEach(row => {
      const tr = document.createElement("tr")
      tr.className = "bg-white"
      const dash = value => this._escape(value || "—")
      tr.innerHTML = `
        <td class="px-2 py-1.5 text-gray-900 break-words">
          ${this._escape(row.title)}
          ${this._trashFallbackLine(row)}
        </td>
        <td class="hidden lg:table-cell px-2 py-1.5 text-center text-gray-600">${dash(row.type)}</td>
        <td class="hidden xl:table-cell px-2 py-1.5 text-center text-gray-600">${dash(row.room)}</td>
        <td class="hidden sm:table-cell px-2 py-1.5 text-center text-gray-600">${dash(row.status)}</td>
        <td class="px-2 py-1.5 text-center text-gray-600">${dash(row.deleted_at)}</td>
        <td class="px-2 py-1.5 text-center text-gray-600 break-words">${dash(row.deleted_by)}</td>
        <td class="px-2 py-1.5 text-right"></td>
      `
      const button = document.createElement("button")
      button.type = "button"
      button.className = "px-3 py-1 text-[13px] text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer"
      button.textContent = "Restore"
      button.addEventListener("click", () => this._restoreTrashRow(row, tr, button))
      tr.lastElementChild.appendChild(button)
      body.appendChild(tr)
    })
  }

  // The secondary line under the project name. Each piece appears only while
  // its own column is hidden, so nothing is lost on a narrow window and
  // nothing is shown twice on a wide one.
  _trashFallbackLine(row) {
    const pieces = [
      ["sm:hidden", row.status],
      ["md:hidden", row.submitted_by],
      ["lg:hidden", row.type],
      ["xl:hidden", row.pm && `PM ${row.pm}`],
      ["xl:hidden", row.room && `Room ${row.room}`],
      ["xl:hidden", row.created_at]
    ].filter(([, value]) => value)

    if (pieces.length === 0) return ""

    const spans = pieces
      .map(([breakpoint, value]) => `<span class="${breakpoint}">${this._escape(value)}</span>`)
      .join("")
    return `<div class="flex flex-wrap gap-x-2 text-gray-400">${spans}</div>`
  }

  // Restoring puts the row back in whichever tab it belongs to, which this
  // page can't know — so reload once it lands.
  _restoreTrashRow(row, tr, button) {
    button.disabled = true
    button.innerHTML = '<span class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'

    const formData = new FormData()
    formData.append("kind", row.kind)
    formData.append("id", row.id)

    fetch("/companies/uni_restore", {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(r => {
      if (!r.ok) throw new Error("restore failed")
      tr.remove()
      this._trashRows = (this._trashRows || []).filter(candidate => !(candidate.kind === row.kind && candidate.id === row.id))
      sessionStorage.setItem("uniFlash", "Restored successfully")
      window.location.reload()
    }).catch(() => {
      button.disabled = false
      button.textContent = "Restore"
      this._showToast("Could not restore that row", "alert")
    })
  }

  _escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  // ─── PDF Export ──────────────────────────────────────────────

  exportPdf(e) {
    e.preventDefault()
    let href = e.currentTarget.href || e.currentTarget.dataset.href
    if (this._sortState.length === 2) {
      const sep = href.includes("?") ? "&" : "?"
      href += `${sep}sort_by=${this._sortState[0]}&sort_order=${this._sortState[1]}`
    }
    window.open(href, "_blank")
  }

  // ─── Modal Helpers ──────────────────────────────────────────

  _showModal(id) {
    const modal = document.getElementById(id)
    if (modal) {
      modal.classList.remove("hidden")
      modal.classList.add("flex")
    }
  }

  _hideModal(id) {
    const modal = document.getElementById(id)
    if (modal) {
      modal.classList.add("hidden")
      modal.classList.remove("flex")
    }
  }

  hideModal(e) {
    // Close modal when clicking backdrop
    if (e.target === e.currentTarget) {
      e.currentTarget.classList.add("hidden")
      e.currentTarget.classList.remove("flex")
    }
  }

  closeModal(e) {
    const modal = e.currentTarget.closest("[id^='uni']")
    if (modal) {
      modal.classList.add("hidden")
      modal.classList.remove("flex")
    }
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // ─── Confirm Modal ──────────────────────────────────────────

  _openConfirmModal(title, message, onConfirm) {
    const modal = document.getElementById("uniConfirmModal")
    modal.querySelector(".js-confirm-title").textContent = title
    modal.querySelector(".js-confirm-message").textContent = message
    this._confirmCallback = onConfirm
    this._showModal("uniConfirmModal")
  }

  confirmAction() {
    if (this._confirmCallback) {
      this._confirmCallback()
      this._confirmCallback = null
    }
    this._hideModal("uniConfirmModal")
  }

  cancelConfirm() {
    this._confirmCallback = null
    this._hideModal("uniConfirmModal")
  }

  // ─── Toast ──────────────────────────────────────────────────

  _showToast(message, type = "notice") {
    const cssClass = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    const toast = document.createElement("div")
    toast.className = cssClass
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.innerHTML = `
      <div class="flex items-center justify-between gap-3">
        <span>${message}</span>
        <button data-action="click->flash#dismiss" class="text-current opacity-60 hover:opacity-100 cursor-pointer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `
    document.body.prepend(toast)
  }

  // ─── Utilities ──────────────────────────────────────────────

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _bindRowHover() {
    this.element.querySelectorAll(".js-data-row").forEach(row => {
      row.addEventListener("mouseenter", () => { row.style.backgroundColor = "" ; row.classList.add("bg-brand/[0.04]") })
      row.addEventListener("mouseleave", () => { row.classList.remove("bg-brand/[0.04]"); row.style.backgroundColor = "" })
    })
  }
}
